//TopSellingSku.jsx
import React, { useState, useEffect } from 'react';
import GradientHeading from './GradientHeading';
import './TopSellingSku.css';
import Loader from './Loader';
// import './modern-date-range.css';
import { API_URL as API_BASE } from '../api/api';

const TopSellingSku = () => {
  const [skuData, setSkuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [topN, setTopN] = useState(10);

  // Set default dates (last 30 days) and fetch data on mount
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const defaultEnd = today.toISOString().split('T')[0];
    const defaultStart = thirtyDaysAgo.toISOString().split('T')[0];
    setEndDate(defaultEnd);
    setStartDate(defaultStart);
  }, []);

  // Fetch data automatically when default dates are set
  useEffect(() => {
    if (startDate && endDate) {
      fetchTopSkus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchTopSkus = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        top_n: topN.toString(),
        page_size: '100',
        page_no: '1'
      });

      console.log('[TopSellingSKU] Fetching top selling SKUs with params:', params.toString());
      const response = await fetch(`${API_BASE}/top_selling_skus?${params}`, {
        credentials: 'include'
        
        // Note: fetch does not support 'withCredentials', that's for axios only.
      });
      console.log('[TopSellingSKU] API response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('[TopSellingSKU] API response data:', data);
      setSkuData(data.top_selling_skus || []);
    } catch (err) {
      setError(`Failed to fetch data: ${err.message}`);
      console.error('Error fetching top selling SKUs:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="top-selling-sku-container">
      <div className="header-section">
        <GradientHeading>Top Selling SKUs</GradientHeading>
        <p className="page-subtitle">Analyze your best performing products</p>
      </div>

      <div className="controls-section-modern">
        {/* <div className="date-range-card-modern"> */}
          <div className="date-range-label">
            <span className="date-range-title">Date Range:</span>
          </div>
          <div className="modern-date-row">
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="modern-date-input"
            />
            <span className="to-label">to</span>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="modern-date-input"
            />
          </div>
        {/* </div> */}
        <div className="modern-topn-group">
          <label htmlFor="top-n" className="modern-label">Top N SKUs:</label>
          <select
            id="top-n"
            value={topN}
            onChange={(e) => setTopN(parseInt(e.target.value))}
            className="modern-select"
          >
            <option value={5}>Top 5</option>
            <option value={10}>Top 10</option>
            <option value={20}>Top 20</option>
            <option value={50}>Top 50</option>
          </select>
        </div>
        <button 
          onClick={fetchTopSkus} 
          disabled={loading || !startDate || !endDate}
          className="modern-fetch-button"
        >
          {loading ? <Loader size="small" /> : 'Get Top SKUs'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {loading && (
        <Loader text="Fetching top selling SKUs..." />
      )}

      {!loading && (
        <div className="results-section">
          <div className="table-container">
            <table className="sku-table">
              <thead>
                <tr>
                  <th className="rank-column">Rank</th>
                  <th className="product-column">Product Details</th>
                  <th className="units-column">Units Sold</th>
                  <th className="revenue-column">Total Sales</th>
                  <th className="price-column">Avg Price</th>
                </tr>
              </thead>
              <tbody>
                {skuData.length > 0 ? (
                  skuData.map((sku, index) => (
                    <tr key={sku.sku_id} className="sku-row">
                      <td className="rank-cell">
                        <span className={`rank-badge rank-${Math.min(index + 1, 3)}`}>
                          #{index + 1}
                        </span>
                      </td>
                      <td className="product-cell">
                        <div className="product-info">
                          <h4 className="product-name">{sku.product_name || 'N/A'}</h4>
                          <p className="sku-name">{sku.sku_name || 'N/A'}</p>
                          <div className="ids">
                            <span className="product-id">Product ID: {sku.product_id}</span>
                            <span className="sku-id">SKU ID: {sku.sku_id}</span>
                          </div>
                        </div>
                      </td>
                      <td className="units-cell">
                        <span className="units-number">{formatNumber(sku.units_sold)}</span>
                      </td>
                      <td className="revenue-cell">
                        <span className="revenue-number">{formatCurrency(sku.total_sales)}</span>
                      </td>
                      <td className="price-cell">
                        <span className="price-number">
                          {formatCurrency(sku.total_sales / sku.units_sold)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#64748b', fontStyle: 'italic', padding: '32px 0' }}>
                      No top selling SKUs found for the selected date range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && skuData.length === 0 && !error && startDate && endDate && (
        <div className="no-data-message">
          <span className="no-data-icon">📊</span>
          <h3>No Data Found</h3>
          <p>No top selling SKUs found for the selected date range.</p>
        </div>
      )}
    </div>
  );
};

export default TopSellingSku;